import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Shift, ShiftListFilters, ShiftsResponse } from '../models/shifts.model';

@Injectable({ providedIn: 'root' })
export class ShiftsService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Fetch paginated shifts
   */
  getShifts(
    pageNumber: number = 1,
    pageSize: number = 10,
    filters?: ShiftListFilters,
  ): Observable<ShiftsResponse | null> {
    let params = new HttpParams()
      .set('page', String(pageNumber))
      .set('pageSize', String(pageSize));

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http.get<ShiftsResponse>(`${this.baseUrl}/api/shifts`, { params }).pipe(
      catchError((err) => {
        console.error('Error fetching shifts:', err);
        return of(null);
      }),
    );
  }

  /**
   * Get shift by ID
   */
  getShiftById(id: number): Observable<Shift | null> {
    return this.http.get<{ status: string; data: Shift }>(`${this.baseUrl}/api/shifts/${id}`).pipe(
      map((res) => res.data),
      catchError((err) => {
        console.error('Error fetching shift by ID:', err);
        return of(null);
      }),
    );
  }

  /**
   * Create shift
   */
  createShift(payload: {
    name: string;
    GracePeriodMinutes: number;
  }): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ status?: string; message?: string }>(`${this.baseUrl}/api/shifts`, payload)
      .pipe(
        map((res) => ({
          success: true,
          message: res?.message || 'Shift created successfully',
        })),
        catchError((err) => {
          console.error('Error creating shift:', err);

          return of({
            success: false,
            message:
              err?.error?.message || err?.error?.title || err?.message || 'Failed to create shift',
          });
        }),
      );
  }

  /**
   * Update shift
   */
  updateShift(
    id: number,
    payload: {
      name?: string;
      GracePeriodMinutes?: number;
    },
  ): Observable<{ success: boolean; message: string }> {
    return this.http
      .patch<{ status?: string; message?: string }>(`${this.baseUrl}/api/shifts/${id}`, payload)
      .pipe(
        map((res) => ({
          success: true,
          message: res?.message || 'Shift updated successfully',
        })),
        catchError((err) => {
          console.error('Error updating shift:', err);

          return of({
            success: false,
            message:
              err?.error?.message || err?.error?.title || err?.message || 'Failed to update shift',
          });
        }),
      );
  }

  /**
   * Delete shift
   */
  deleteShift(id: number): Observable<{ success: boolean; message: string }> {
    return this.http
      .delete<{ status?: string; message?: string }>(`${this.baseUrl}/api/shifts/${id}`)
      .pipe(
        map((res) => ({
          success: true,
          message: res?.message || 'Shift deleted successfully',
        })),
        catchError((err) => {
          console.error('Error deleting shift:', err);

          return of({
            success: false,
            message:
              err?.error?.message || err?.error?.title || err?.message || 'Failed to delete shift',
          });
        }),
      );
  }
}